
import { put, call, takeEvery, select, all } from "redux-saga/effects";
import _ from 'lodash';
import moment from 'moment-timezone';
import { getJwtToken, getDataFromKpiDash, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import assetConfig from "../../utils/pictree/AssetConfig";

import {
  setPageTopLoader,
  setRewardCoinSummary,
  setRewardCoinData,
  setRewardCoinRankingData,
  setRewardedCoinData,
} from 'actions';

export const dateRange = (state) => state.page.dateRangePicTree;
export const startDate = state => state.page.dateRangePicTree[0];
export const endDate = state => state.page.dateRangePicTree[1];
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const selectedSeasonNtt = (state) => state.pictreeNtt.selectedSeason;

const buildSeasonCondition = (columnName, seasonIds) => {
  if (!seasonIds || seasonIds.length === 0) return "";
  const ids = seasonIds.map((id) => `'${id}'`).join(", ");
  return seasonIds.length === 1
    ? `AND ${columnName} = '${seasonIds[0]}'`
    : `AND ${columnName} IN (${ids})`;
};

const buildRewardTypeBySeasonQuery = (seasonIds) => ({
  query: `
    SELECT
      t.game_space_id,
      EXISTS(
        SELECT 1
        FROM balance_change_event_of_discovery_report_reward AS bceodrrb
        JOIN report AS r ON r.id = bceodrrb.discovery_report_id
        JOIN balance_change_history AS bch ON bch.source_event_id = bceodrrb.id
        WHERE r.game_space_id = t.game_space_id
          AND r.type = 'DISCOVERY'
          AND r.status = 'APPROVED'
          AND bch.coin_type = 'REWARD'
          AND bch.amount > 0
        LIMIT 1
      ) AS isDiscoveryReportReward,
      EXISTS(
        SELECT 1
        FROM balance_change_event_of_photograph AS bceop
        JOIN balance_change_history AS bch ON bch.source_event_id = bceop.id
        WHERE bceop.game_space_id = t.game_space_id
          AND bch.coin_type = 'REWARD'
          AND bch.amount > 0
        LIMIT 1
      ) AS isPhotographReward
    FROM (
      ${seasonIds.map((id) => `SELECT '${id}' AS game_space_id`).join(" UNION ALL ")}
    ) AS t
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildPhotographRewardQuery = (rangeTarget, normalSeasonIds) => ({
  query: `
    SELECT "撮影報酬" AS reward_type, pa.type, pa.sub_type, bch.amount AS amount, SUM(bch.amount) AS total_amount, COUNT(*) AS count
    FROM balance_change_history bch
    JOIN balance_change_event bce ON bch.source_event_id = bce.id
    JOIN balance_change_event_of_photograph bceop ON bceop.id = bce.id
    JOIN power_asset pa ON pa.id = bceop.power_asset_id
    WHERE CONVERT_TZ(bch.occurred_datetime, 'UTC', 'Asia/Tokyo')
    BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
    AND bch.coin_type in ("REWARD") AND bch.amount > 0
    ${buildSeasonCondition("bceop.game_space_id", normalSeasonIds)}
    GROUP BY reward_type, pa.type, bch.amount, pa.sub_type
    ORDER BY amount DESC, pa.type ASC, pa.sub_type ASC
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildDiscoveryRewardQuery = (rangeTarget, crowSeasonIds) => ({
  query: `
    SELECT "発見報酬" AS reward_type, pa.type, pa.sub_type, bch.amount AS amount, SUM(bch.amount) AS total_amount, COUNT(*) AS count
    FROM balance_change_history bch
    JOIN balance_change_event bce ON bch.source_event_id = bce.id
    JOIN balance_change_event_of_discovery_report_reward bceodrrb ON bceodrrb.id = bce.id
    JOIN report r ON r.id = bceodrrb.discovery_report_id AND r.type = "DISCOVERY" AND r.status = "APPROVED"
    JOIN power_asset pa ON pa.id = r.power_asset_id
    WHERE CONVERT_TZ(bch.occurred_datetime, 'UTC', 'Asia/Tokyo')
    BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
    AND bch.coin_type in ("REWARD") AND bch.amount > 0
    ${buildSeasonCondition("r.game_space_id", crowSeasonIds)}
    GROUP BY reward_type, pa.type, bch.amount, pa.sub_type
    ORDER BY amount DESC, pa.type ASC, pa.sub_type ASC
  `,
  database: "KpiDashboard",
  type: "list",
});

const mergeRewardedCoinRows = (rows) => {
  const merged = new Map();
  _.each(rows, (row) => {
    const rewardType = row.reward_type || "";
    const type = row.type || "";
    const subType = row.sub_type || "";
    const amount = Number(row.amount || 0);
    const key = `${rewardType}|${type}|${subType}|${amount}`;
    if (!merged.has(key)) {
      merged.set(key, {
        reward_type: rewardType,
        type,
        sub_type: subType,
        amount,
        total_amount: 0,
        count: 0,
      });
    }
    const current = merged.get(key);
    current.total_amount += Number(row.total_amount || 0);
    current.count += Number(row.count || 0);
  });
  return Array.from(merged.values()).sort((a, b) => {
    if (b.amount !== a.amount) return b.amount - a.amount;
    if (a.reward_type !== b.reward_type) return a.reward_type.localeCompare(b.reward_type);
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return (a.sub_type || "").localeCompare(b.sub_type || "");
  });
};

export function* handleRewardCoinSummaryData() {
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `AND bceop.game_space_id = ${ids}` // ids は "'id1'"
          : `AND bceop.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    let rewardSummaryData = {
      amount: "loading",
      count: "loading",
      players: "loading",
      avgByCount: "loading",
      avgByPlayers: "loading",
    };
    yield put(setRewardCoinSummary(rewardSummaryData));

    const sqlQuery = {
      query: `
        SELECT SUM(bch.amount) AS amount, COUNT(*) AS count, COUNT(DISTINCT(bce.player_id)) AS players
        FROM balance_change_history bch
        JOIN balance_change_event bce ON bch.source_event_id = bce.id
        JOIN balance_change_event_of_photograph bceop ON bceop.id = bce.id
        JOIN player_state ps ON bce.player_id = ps.player_id
        WHERE CONVERT_TZ(bch.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
        AND bch.coin_type in ("REWARD") AND bch.amount > 0
        ${seasonQuery};
      `,
      database: "KpiDashboard",
      type: "list",
    }

    const [dataTemp] =
    yield all([
      call(getDataFromSxi, sqlQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
    ]);
    const summaryData = dataTemp;
    rewardSummaryData = {
      amount: summaryData[0]["amount"],
      count: summaryData[0]["count"],
      players: summaryData[0]["players"],
      avgByCount: summaryData[0]["amount"] / summaryData[0]["count"],
      avgByPlayers: summaryData[0]["amount"] / summaryData[0]["players"],
    };
    yield put(setRewardCoinSummary(rewardSummaryData));
  } catch (error) {
    console.log(error);
  }
}

export function* handleRewardCoinData() {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const sqlQuery = {
      query: `
        SELECT
          bch.amount AS reward_coin_amount,
          bce.player_id AS player_id,
          ps.name AS name,
          ps.team_code,
          DATE_FORMAT(CONVERT_TZ(bch.occurred_datetime, '+00:00', '+09:00'), '%Y-%m-%d %H:%i:%s') AS occurred_datetime_jst
        FROM balance_change_history bch
        JOIN balance_change_event bce ON bch.source_event_id = bce.id
        JOIN player_state ps ON bce.player_id = ps.player_id
        WHERE CONVERT_TZ(bch.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
        AND bch.coin_type in ("REWARD") AND bch.amount > 0
        ORDER BY bch.occurred_datetime DESC;
      `
    }

    const [dataRankingTemp] =
    yield all([
      call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API),
    ]);
    const rankingData = JSON.parse(dataRankingTemp.body);
    let rankingTableData = [];
    _.each(rankingData, function(d, index) {
      rankingTableData.push([
        index+1,
        d.player_id,
        d.name,
        d.team_code,
        d.reward_coin_amount,
        d.occurred_datetime_jst,
      ]);
    })
    yield put(setRewardCoinData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export function* handleRewardCoinRankingData() {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `AND bceop.game_space_id = ${ids}` // ids は "'id1'"
          : `AND bceop.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const sqlQueryRanking = {
      query: `
        SELECT SUM(bch.amount) AS reward_coin_amount, COUNT(*) AS count, bce.player_id AS player_id, ps.name AS name
        FROM balance_change_history bch
        JOIN balance_change_event bce ON bch.source_event_id = bce.id
        JOIN balance_change_event_of_photograph bceop ON bceop.id = bce.id
        JOIN player_state ps ON bce.player_id = ps.player_id
        WHERE CONVERT_TZ(bch.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
        AND bch.coin_type in ("REWARD") AND bch.amount > 0
        ${seasonQuery}
        GROUP BY player_id
        ORDER BY reward_coin_amount DESC;
      `,
      database: "KpiDashboard",
      type: "list",
    }

    const [dataRankingTemp] =
    yield all([
      call(getDataFromSxi, sqlQueryRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
    ]);
    // const rankingData = JSON.parse(dataRankingTemp.body);
    const rankingData = dataRankingTemp;
    const totalReward = _.sumBy(rankingData, 'reward_coin_amount');
    let rankingTableData = [];
    _.each(rankingData, function(d, index) {
      const ratio = (d.reward_coin_amount / totalReward * 100).toFixed(2);
      rankingTableData.push([
        index+1,
        d.player_id,
        d.name,
        d.count,
        d.reward_coin_amount,
        ratio,
      ]);
    })
    yield put(setRewardCoinRankingData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export function* handleRewardedCoinData() {
  try {
    yield put(setPageTopLoader(true));
    yield put(setRewardedCoinData([]));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const selectedSeasonIds = (selectedSeasonData || [])
      .map((s) => s && s.seasonId)
      .filter(Boolean);
    const hasSelectedSeason = selectedSeasonIds.length > 0;
    let discoverySeasonIds = [];
    let photographSeasonIds = [];

    if (hasSelectedSeason) {
      const rewardTypeBySeason = yield call(
        getDataFromSxi,
        buildRewardTypeBySeasonQuery(selectedSeasonIds),
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      );

      const rewardTypeMap = new Map(
        _.map(rewardTypeBySeason, (row) => [
          row.game_space_id,
          {
            isDiscoveryReportReward: Number(row.isDiscoveryReportReward) === 1,
            isPhotographReward: Number(row.isPhotographReward) === 1,
          },
        ])
      );

      discoverySeasonIds = selectedSeasonIds.filter(
        (id) => rewardTypeMap.get(id) && rewardTypeMap.get(id).isDiscoveryReportReward
      );
      photographSeasonIds = selectedSeasonIds.filter(
        (id) => rewardTypeMap.get(id) && rewardTypeMap.get(id).isPhotographReward
      );
    }

    const queryCalls = [];
    if (!hasSelectedSeason || photographSeasonIds.length > 0) {
      queryCalls.push(
        call(
          getDataFromSxi,
          buildPhotographRewardQuery(rangeTarget, hasSelectedSeason ? photographSeasonIds : []),
          ENDPOINT.GET_DATA_SXI_DENCHU,
          jwtToken
        )
      );
    }
    if (!hasSelectedSeason || discoverySeasonIds.length > 0) {
      queryCalls.push(
        call(
          getDataFromSxi,
          buildDiscoveryRewardQuery(rangeTarget, hasSelectedSeason ? discoverySeasonIds : []),
          ENDPOINT.GET_DATA_SXI_DENCHU,
          jwtToken
        )
      );
    }

    const queryResults = queryCalls.length > 0 ? yield all(queryCalls) : [];
    const data = mergeRewardedCoinRows(_.flatten(queryResults));
    const totalReward = _.sumBy(data, 'total_amount');
    let tableData = [];
    const getLabelByTypeAndSubType = (type, subType) => {
      if (type === "MAN_HOLE" && subType === "NORMAL") return "マンホール";
      if (type === "HAND_HOLE" && subType === "NORMAL") return "ハンドホール";
      return assetConfig.assetSubType[subType] || subType || type;
    };
    _.each(data, function(d, index) {
      const ratio = (d.total_amount / totalReward * 100).toFixed(2);
      tableData.push([
        index+1,
        getLabelByTypeAndSubType(d.type, d.sub_type),
        d.reward_type,
        d.amount,
        d.total_amount,
        d.count,
        ratio,
      ]);
    })
    yield put(setRewardedCoinData(tableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export default function* watchMapSettings() {
  yield takeEvery(PICTREE.GET_REWARD_COIN_SUMMARY_DATA, handleRewardCoinSummaryData);
  yield takeEvery(PICTREE.GET_REWARD_COIN_DATA, handleRewardCoinData);
  yield takeEvery(PICTREE.GET_REWARD_COIN_RANKING_DATA, handleRewardCoinRankingData);
  yield takeEvery(PICTREE.GET_REWARDED_COIN_DATA, handleRewardedCoinData);
}
